(() => {
  if (window.ZicoSearchRelevance) return;

  const SEARCH_PATH = '/search';
  const STRICT_MODE = 'strict';
  const BROAD_MODE = 'broad';
  const STOP_WORDS = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'on', 'or', 'the', 'to', 'with']);
  const TITLE_PHRASE_ALIASES = [
    ['half tights', ['half', 'tight']],
    ['half tight', ['half', 'tight']],
    ['short tights', ['short', 'tight']],
    ['race tights', ['race', 'tight']],
    ['running socks', ['running', 'sock']],
    ['running sock', ['running', 'sock']],
    ['race socks', ['race', 'sock']],
    ['race sock', ['race', 'sock']],
    ['t shirts', ['shirt']],
    ['t shirt', ['shirt']],
    ['tee shirts', ['shirt']],
    ['tee shirt', ['shirt']],
    ['singlets', ['singlet']],
    ['singlet', ['singlet']],
    ['vests', ['vest']],
    ['vest', ['vest']],
    ['shorts', ['shorts']],
    ['short', ['short']],
    ['hoodies', ['hoodie']],
    ['hoodie', ['hoodie']],
    ['caps', ['cap']],
    ['cap', ['cap']],
    ['hat', ['hat']],
  ];
  const EXACT_QUERY_ALIASES = new Map([
    ['001', { vendor: 'norda', title: ['001'] }],
    ['001a', { vendor: 'norda', title: ['001a'] }],
    ['norda 001', { vendor: 'norda', title: ['001'] }],
    ['norda 001a', { vendor: 'norda', title: ['001a'] }],
    ['gel kahana', { vendor: 'asics', title: ['kahana'] }],
    ['gel-kahana', { vendor: 'asics', title: ['kahana'] }],
    ['kahana', { vendor: 'asics', title: ['kahana'] }],
    ['mothtech', { vendor: 'satisfy', title: ['mothtech'] }],
    ['moth tech', { vendor: 'satisfy', title: ['mothtech'] }],
    ['r1', { vendor: 'mount to coast', title: ['r1'] }],
    ['team 247', { vendor: 'represent', title: ['247'] }],
    ['represent 247', { vendor: 'represent', title: ['247'] }],
  ]);
  const BRAND_ALIASES = [
    ['mount to coast', 'mount to coast'],
    ['currently running', 'currently running'],
    ['bandit running', 'bandit running'],
    ['human made', 'human made'],
    ['arc teryx', 'arc teryx'],
    ['participation', 'participation'],
    ['undefeated', 'undefeated'],
    ['essentials', 'essentials'],
    ['represent', 'represent'],
    ['satisfy', 'satisfy'],
    ['stussy', 'stussy'],
    ['bandit', 'bandit running'],
    ['norda', 'norda'],
    ['mizuno', 'mizuno'],
    ['asics', 'asics'],
    ['bape', 'bape'],
    ['nike', 'nike'],
    ['uvu', 'uvu'],
    ['eou', 'eou'],
  ];

  const normalize = (value) => (value || '').normalize('NFKC').replace(/\s+/g, ' ').trim();

  const tokenize = (value, options = {}) => {
    const tokens = normalize(value)
      .toLowerCase()
      .replace(/[â€™']/g, ' ')
      .match(/[\p{L}\p{N}\p{M}]+/gu) || [];
    const output = [];

    tokens.forEach((rawToken) => {
      let token = rawToken;
      if (!options.keepStopWords && STOP_WORDS.has(token)) return;
      if (token.length === 1 && !/^\d$/.test(token)) return;
      if (!options.keepPlural && /^[a-z]{4,}s$/.test(token) && !token.endsWith('ss')) {
        token = token.slice(0, -1);
      }
      if (!output.includes(token)) output.push(token);
    });

    return output.slice(0, 8);
  };

  const findBrand = (tokens) => {
    for (const [alias, vendor] of BRAND_ALIASES) {
      const aliasTokens = alias.split(' ');
      for (let index = 0; index <= tokens.length - aliasTokens.length; index += 1) {
        if (aliasTokens.every((token, offset) => tokens[index + offset] === token)) {
          return { index, length: aliasTokens.length, vendor };
        }
      }
    }
    return null;
  };

  const compactKey = (value) => tokenize(value, { keepStopWords: true, keepPlural: true }).join(' ');

  const addVendorClauses = (clauses, vendor) => {
    tokenize(vendor, { keepStopWords: true, keepPlural: true }).forEach((token) => {
      clauses.push(`vendor:${token}`);
    });
  };

  const findTitleAlias = (tokens) => {
    for (const [alias, mappedTokens] of TITLE_PHRASE_ALIASES) {
      const aliasTokens = alias.split(' ');
      for (let index = 0; index <= tokens.length - aliasTokens.length; index += 1) {
        if (aliasTokens.every((token, offset) => tokens[index + offset] === token)) {
          return { index, length: aliasTokens.length, mappedTokens };
        }
      }
    }
    return null;
  };

  const buildAdvancedQuery = (plainTerm) => {
    const allTokens = tokenize(plainTerm, { keepStopWords: true, keepPlural: true });
    if (!allTokens.length) return '';

    const exactAlias = EXACT_QUERY_ALIASES.get(compactKey(plainTerm));
    if (exactAlias) {
      const clauses = [];
      addVendorClauses(clauses, exactAlias.vendor);
      exactAlias.title.forEach((token) => clauses.push(`title:${token}*`));
      return clauses.join(' AND ');
    }

    const brand = findBrand(allTokens);
    let remainingTokens = brand
      ? allTokens.filter((_, index) => index < brand.index || index >= brand.index + brand.length)
      : allTokens;
    const titleAlias = findTitleAlias(remainingTokens);
    let titleTokens;
    if (titleAlias) {
      titleTokens = titleAlias.mappedTokens;
      remainingTokens = remainingTokens.filter(
        (_, index) => index < titleAlias.index || index >= titleAlias.index + titleAlias.length
      );
      titleTokens = titleTokens.concat(tokenize(remainingTokens.join(' ')));
    } else {
      titleTokens = tokenize(remainingTokens.join(' '));
    }
    const clauses = [];

    if (brand) {
      addVendorClauses(clauses, brand.vendor);
    }

    titleTokens.forEach((token) => clauses.push(`title:${token}*`));
    return clauses.join(' AND ');
  };

  const isAdvancedQuery = (term) => /(?:^|\s)(?:title|vendor):/i.test(term || '');

  const buildSearchUrl = (plainTerm, mode = STRICT_MODE, baseUrl = window.location.origin + SEARCH_PATH) => {
    const plain = normalize(plainTerm);
    const url = new URL(baseUrl, window.location.origin);
    const advanced = buildAdvancedQuery(plain);

    url.pathname = SEARCH_PATH;
    url.search = '';
    url.searchParams.set('q', mode === STRICT_MODE && advanced ? advanced : plain);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', mode === STRICT_MODE && advanced ? 'none' : 'last');
    url.searchParams.set('zq', plain);
    url.searchParams.set('zmode', mode === STRICT_MODE && advanced ? STRICT_MODE : BROAD_MODE);
    return url;
  };

  const getState = () => {
    const url = new URL(window.location.href);
    return {
      url,
      query: normalize(url.searchParams.get('q')),
      plain: normalize(url.searchParams.get('zq')),
      mode: url.searchParams.get('zmode') === BROAD_MODE ? BROAD_MODE : STRICT_MODE,
    };
  };

  const directState = getState();
  if (
    window.location.pathname === SEARCH_PATH &&
    directState.query &&
    !directState.plain &&
    !isAdvancedQuery(directState.query)
  ) {
    window.location.replace(buildSearchUrl(directState.query).toString());
    return;
  }

  const setHiddenInput = (form, name, value) => {
    let input = Array.from(form.elements).find(
      (element) => element instanceof HTMLInputElement && element.type === 'hidden' && element.name === name
    );
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    if (input.value !== value) input.value = value;
  };

  const restorePlainTerm = () => {
    if (window.location.pathname !== SEARCH_PATH) return;
    const state = getState();
    if (!state.plain) return;

    document.querySelectorAll('form[action*="/search"] input[type="search"][name="q"]').forEach((input) => {
      if (input.value === state.query) input.value = state.plain;
    });

    if (state.query && document.title.includes(state.query)) {
      document.title = document.title.replace(state.query, state.plain);
    }
  };

  const preserveSearchState = () => {
    if (window.location.pathname !== SEARCH_PATH) return;
    const state = getState();
    if (!state.query || !state.plain) return;
    const prefix = state.mode === BROAD_MODE ? 'last' : 'none';

    document.querySelectorAll('form').forEach((form) => {
      const action = new URL(form.getAttribute('action') || window.location.href, window.location.href);
      if (action.origin !== window.location.origin || action.pathname !== SEARCH_PATH) return;

      const visibleQuery = form.querySelector('input[type="search"][name="q"]');
      if (!visibleQuery) setHiddenInput(form, 'q', state.query);
      setHiddenInput(form, 'type', 'product');
      setHiddenInput(form, 'options[prefix]', prefix);
      setHiddenInput(form, 'zq', state.plain);
      setHiddenInput(form, 'zmode', state.mode);
    });

    document
      .querySelectorAll(
        'facet-filters-form a[href], .active-facets a[href], .pagination-wrapper a[href], #FacetsWrapperDesktop a[href], .mobile-facets a[href], #main-search-filters a[href]'
      )
      .forEach((anchor) => {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== SEARCH_PATH || !url.searchParams.has('q')) return;
        if (url.searchParams.get('type') !== 'product') url.searchParams.set('type', 'product');
        if (url.searchParams.get('options[prefix]') !== prefix) url.searchParams.set('options[prefix]', prefix);
        if (url.searchParams.get('zq') !== state.plain) url.searchParams.set('zq', state.plain);
        if (url.searchParams.get('zmode') !== state.mode) url.searchParams.set('zmode', state.mode);
        if (anchor.href !== url.toString()) anchor.href = url.toString();
      });
  };

  const fallbackIfEmpty = () => {
    if (window.location.pathname !== SEARCH_PATH) return;
    const state = getState();
    if (state.mode !== STRICT_MODE || !state.plain || !isAdvancedQuery(state.query)) return;
    if (Array.from(state.url.searchParams.keys()).some((key) => key.startsWith('filter.')) || state.url.searchParams.has('page')) return;

    const grid = document.querySelector('#product-grid');
    if (grid && !grid.querySelector('.grid__item')) {
      window.location.replace(buildSearchUrl(state.plain, BROAD_MODE, state.url).toString());
    }
  };

  const enhancePage = () => {
    restorePlainTerm();
    preserveSearchState();
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = new URL(form.getAttribute('action') || window.location.href, window.location.href);
      if (action.origin !== window.location.origin || action.pathname !== SEARCH_PATH) return;
      if (form.closest('predictive-search')?.querySelector('[aria-selected="true"] a')) return;

      const input = form.querySelector('input[type="search"][name="q"]');
      if (!input) return;
      const plain = normalize(input.value);
      if (!plain || isAdvancedQuery(plain)) return;

      event.preventDefault();
      window.location.assign(buildSearchUrl(plain).toString());
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target?.closest?.('predictive-search a[href]');
      if (!anchor) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== SEARCH_PATH) return;
      const plain = normalize(url.searchParams.get('q'));
      if (!plain || isAdvancedQuery(plain)) return;

      event.preventDefault();
      window.location.assign(buildSearchUrl(plain).toString());
    },
    true
  );

  let enhanceScheduled = false;
  const scheduleEnhance = () => {
    if (enhanceScheduled) return;
    enhanceScheduled = true;
    window.requestAnimationFrame(() => {
      enhanceScheduled = false;
      enhancePage();
    });
  };

  const start = () => {
    enhancePage();
    fallbackIfEmpty();
  };

  window.addEventListener('popstate', scheduleEnhance);
  window.addEventListener('pageshow', scheduleEnhance);
  document.addEventListener('shopify:section:load', scheduleEnhance);
  document.addEventListener('zico:facets-rendered', scheduleEnhance);
  window.ZicoSearchRelevance = {
    buildAdvancedQuery,
    buildPredictiveQuery: buildAdvancedQuery,
    buildSearchUrl,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
